import { useEffect, useRef, useState } from 'react';
import { callApi } from './api';
import { getStoredAdminToken, saveAdminToken } from './adminToken';
import GenerationPanel from './GenerationPanel';

const EMPTY_DRAFT = { label: '', prompt: '', style: 'bullet' };

/**
 * 各種会議用：種別（活動アップ／委員会など）を選んで生成する画面。
 * 種別・項目は固定リストではなく、管理者パスワードを解除すると
 * その場で自由に追加・改名・削除できる（担当者会議録用の部署とは異なり、
 * ここは職員が運用しながら育てていく想定のため）。
 */
export default function ConferenceScreen({ onBack }) {
  const [templates, setTemplates] = useState(null); // null=読み込み中
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState('');

  const [managing, setManaging] = useState(false);
  const [adminToken, setAdminToken] = useState(() => getStoredAdminToken());
  const [password, setPassword] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');
  const unlockingRef = useRef(false);

  const [editingId, setEditingId] = useState('');
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState('');
  const [typeError, setTypeError] = useState('');

  const [newTypeName, setNewTypeName] = useState('');
  const [creatingType, setCreatingType] = useState(false);

  const [itemDrafts, setItemDrafts] = useState({}); // itemId -> {label, prompt, style}
  const [newItemDraft, setNewItemDraft] = useState(EMPTY_DRAFT);
  const [itemBusyId, setItemBusyId] = useState(''); // 保存/削除/追加のいずれかで処理中のID('__new__'含む)
  const [itemSavedId, setItemSavedId] = useState('');
  const [itemErrors, setItemErrors] = useState({});

  function loadConfig() {
    return callApi('config').then((data) => {
      const conferences = (data.templates || []).filter((t) => t.kind === 'conference');
      setTemplates(conferences);
      return conferences;
    });
  }

  useEffect(() => {
    let cancelled = false;
    callApi('config')
      .then((data) => {
        if (cancelled) return;
        setTemplates((data.templates || []).filter((t) => t.kind === 'conference'));
      })
      .catch((err) => { if (!cancelled) setLoadError(err.message); });
    return () => { cancelled = true; };
  }, []);

  const editingTemplate = templates?.find((t) => t.id === editingId);

  useEffect(() => {
    if (!editingTemplate) return;
    setNameDraft(editingTemplate.display_name);
    const drafts = {};
    editingTemplate.items.forEach((it) => { drafts[it.id] = { label: it.label, prompt: it.prompt, style: it.style }; });
    setItemDrafts(drafts);
    setNewItemDraft(EMPTY_DRAFT);
    setTypeError('');
    setItemErrors({});
  }, [editingTemplate]);

  async function handleUnlock(e) {
    e.preventDefault();
    if (!password || unlockingRef.current) return;
    unlockingRef.current = true;
    setUnlocking(true);
    setUnlockError('');
    try {
      const data = await callApi('admin_login', { password });
      saveAdminToken(data.token);
      setAdminToken(data.token);
      setPassword('');
      setManaging(true);
    } catch (err) {
      setUnlockError(err.message);
    } finally {
      unlockingRef.current = false;
      setUnlocking(false);
    }
  }

  function handleAdminExpired(err) {
    if (err.code === 'ADMIN_TOKEN_INVALID') setAdminToken(null);
  }

  async function handleCreateType(e) {
    e.preventDefault();
    if (!newTypeName.trim() || creatingType) return;
    setCreatingType(true);
    setTypeError('');
    try {
      const data = await callApi('template_create', { kind: 'conference', displayName: newTypeName.trim() }, adminToken);
      setNewTypeName('');
      const list = await loadConfig();
      const created = list.find((t) => t.id === data.template.id);
      setEditingId(created ? created.id : data.template.id);
    } catch (err) {
      handleAdminExpired(err);
      setTypeError(err.message);
    } finally {
      setCreatingType(false);
    }
  }

  async function handleRenameType() {
    if (!editingId || !nameDraft.trim() || nameSaving) return;
    setNameSaving(true);
    setTypeError('');
    try {
      await callApi('template_rename', { templateId: editingId, displayName: nameDraft.trim() }, adminToken);
      await loadConfig();
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2000);
    } catch (err) {
      handleAdminExpired(err);
      setTypeError(err.message);
    } finally {
      setNameSaving(false);
    }
  }

  async function handleDeleteType(id) {
    if (deleteConfirmId !== id) {
      setDeleteConfirmId(id);
      return;
    }
    setDeleteConfirmId('');
    try {
      await callApi('template_delete', { templateId: id }, adminToken);
      if (editingId === id) setEditingId('');
      if (selectedId === id) setSelectedId('');
      await loadConfig();
    } catch (err) {
      handleAdminExpired(err);
      setTypeError(err.message);
    }
  }

  function updateItemDraft(itemId, patch) {
    setItemDrafts((prev) => ({ ...prev, [itemId]: { ...prev[itemId], ...patch } }));
  }

  async function handleSaveItem(itemId) {
    const draft = itemDrafts[itemId];
    if (!draft?.label.trim() || itemBusyId) return;
    setItemBusyId(itemId);
    setItemErrors((prev) => ({ ...prev, [itemId]: '' }));
    try {
      await callApi('item_update', { itemId, label: draft.label.trim(), prompt: draft.prompt, style: draft.style }, adminToken);
      await loadConfig();
      setItemSavedId(itemId);
      setTimeout(() => setItemSavedId((k) => (k === itemId ? '' : k)), 2000);
    } catch (err) {
      handleAdminExpired(err);
      setItemErrors((prev) => ({ ...prev, [itemId]: err.message }));
    } finally {
      setItemBusyId('');
    }
  }

  async function handleDeleteItem(itemId) {
    if (itemBusyId) return;
    setItemBusyId(itemId);
    try {
      await callApi('item_delete', { itemId }, adminToken);
      await loadConfig();
    } catch (err) {
      handleAdminExpired(err);
      setItemErrors((prev) => ({ ...prev, [itemId]: err.message }));
    } finally {
      setItemBusyId('');
    }
  }

  async function handleAddItem(e) {
    e.preventDefault();
    if (!newItemDraft.label.trim() || itemBusyId) return;
    setItemBusyId('__new__');
    setItemErrors((prev) => ({ ...prev, __new__: '' }));
    try {
      await callApi('item_create', {
        templateId: editingId, label: newItemDraft.label.trim(), prompt: newItemDraft.prompt, style: newItemDraft.style,
      }, adminToken);
      setNewItemDraft(EMPTY_DRAFT);
      await loadConfig();
    } catch (err) {
      handleAdminExpired(err);
      setItemErrors((prev) => ({ ...prev, __new__: err.message }));
    } finally {
      setItemBusyId('');
    }
  }

  const selectedTemplate = templates?.find((t) => t.id === selectedId);

  return (
    <div className="workspace is-wide">
      <div className="workspace-head reveal r1">
        <button className="back-link" type="button" onClick={onBack}>← ホームへ戻る</button>
        <h1 className="workspace-title">各種会議用</h1>
      </div>

      <div className="notice reveal r2">
        利用者様・ご家族のお名前、住所、電話番号、被保険者番号は入力しないでください。
        貼り付ける前に、氏名は「A様」「B様」などに置き換えてください。
      </div>

      <div className="conf-mode-row reveal r2">
        <button
          type="button"
          className="admin-link"
          onClick={() => { setManaging((m) => !m); setEditingId(''); }}
        >
          {managing ? '編集を終える' : '⚙ 種別を管理する'}
        </button>
      </div>

      {templates === null && !loadError && <p className="workspace-loading reveal r3">種別を読み込んでいます…</p>}
      {loadError && <p className="pin-error reveal r3">{loadError}</p>}

      {managing && !adminToken && (
        <form className="editor-card reveal r3" onSubmit={handleUnlock}>
          <p className="admin-lock-note">種別の追加・編集には、管理者のパスワードが必要です。</p>
          <div className="admin-lock-row">
            <input
              type="password" className="admin-password-input" placeholder="パスワード" autoComplete="off"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setUnlockError(''); }}
              disabled={unlocking}
            />
            <button className="enter admin-unlock-btn" type="submit" disabled={!password || unlocking}>
              {unlocking ? '確認中…' : '開く'}
            </button>
          </div>
          {unlockError && <p className="pin-error" role="alert">{unlockError}</p>}
        </form>
      )}

      {!managing && templates && (
        templates.length > 0 ? (
          <div className="dept-grid reveal r3" role="group" aria-label="種別を選択">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`dept-pill${t.id === selectedId ? ' is-selected' : ''}`}
                onClick={() => setSelectedId(t.id)}
              >
                {t.display_name}
              </button>
            ))}
          </div>
        ) : (
          <p className="workspace-loading reveal r3">
            まだ種別がありません。「⚙ 種別を管理する」から追加してください。
          </p>
        )
      )}

      {!managing && selectedTemplate && (
        <GenerationPanel
          key={selectedTemplate.id}
          templateId={selectedTemplate.id}
          templateLabel={selectedTemplate.display_name}
          wordExport={{ meetingName: selectedTemplate.display_name }}
          templateItems={selectedTemplate.items}
        />
      )}

      {managing && adminToken && templates && (
        <>
          <p className="admin-unlocked-note reveal r3">
            編集できます（約30分間）。編集する種別を選んでください。
          </p>

          <div className="dept-grid reveal r3" role="group" aria-label="編集する種別を選択">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`dept-pill${t.id === editingId ? ' is-selected' : ''}`}
                onClick={() => setEditingId(t.id)}
              >
                {t.display_name}
              </button>
            ))}
          </div>

          <form className="editor-card reveal" onSubmit={handleCreateType}>
            <h2 className="admin-dept-title">新しい種別を追加</h2>
            <div className="admin-lock-row">
              <input
                type="text" className="admin-password-input" placeholder="例：〇〇委員会"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                disabled={creatingType}
              />
              <button className="enter admin-unlock-btn" type="submit" disabled={!newTypeName.trim() || creatingType}>
                {creatingType ? '追加中…' : '追加する'}
              </button>
            </div>
          </form>

          {editingTemplate && (
            <div className="editor-card reveal">
              <h2 className="admin-dept-title">「{editingTemplate.display_name}」を編集</h2>

              <div className="admin-lock-row">
                <input
                  type="text" className="admin-password-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
                <button className="enter admin-unlock-btn" type="button" disabled={!nameDraft.trim() || nameSaving} onClick={handleRenameType}>
                  {nameSaving ? '保存中…' : '名前を保存'}
                </button>
                {nameSaved && <span className="admin-saved-label">保存しました</span>}
              </div>

              <div className="admin-save-row">
                <button
                  type="button"
                  className="pii-replace-btn"
                  onClick={() => handleDeleteType(editingTemplate.id)}
                >
                  {deleteConfirmId === editingTemplate.id ? 'もう一度押すと削除します' : 'この種別を削除'}
                </button>
              </div>
              {typeError && <p className="pin-error" role="alert">{typeError}</p>}

              <hr className="conf-divider" />

              {editingTemplate.items.map((it) => {
                const draft = itemDrafts[it.id] ?? { label: it.label, prompt: it.prompt, style: it.style };
                return (
                  <div className="conf-item-editor" key={it.id}>
                    <div className="admin-lock-row">
                      <input
                        type="text" className="admin-password-input" placeholder="項目名"
                        value={draft.label}
                        onChange={(e) => updateItemDraft(it.id, { label: e.target.value })}
                      />
                      <label className="conf-style-check">
                        <input
                          type="checkbox"
                          checked={draft.style === 'narrative'}
                          onChange={(e) => updateItemDraft(it.id, { style: e.target.checked ? 'narrative' : 'bullet' })}
                        />
                        文章でまとめる
                      </label>
                    </div>
                    <textarea
                      className="editor-textarea conf-item-prompt"
                      placeholder="この項目をどうまとめるか（プロンプト）"
                      value={draft.prompt}
                      onChange={(e) => updateItemDraft(it.id, { prompt: e.target.value })}
                    />
                    <div className="admin-save-row">
                      <button
                        className="enter admin-save-btn" type="button"
                        disabled={itemBusyId === it.id}
                        onClick={() => handleSaveItem(it.id)}
                      >
                        {itemBusyId === it.id ? '処理中…' : '保存'}
                      </button>
                      <button
                        type="button" className="pii-replace-btn"
                        disabled={itemBusyId === it.id}
                        onClick={() => handleDeleteItem(it.id)}
                      >
                        削除
                      </button>
                      {itemSavedId === it.id && <span className="admin-saved-label">保存しました</span>}
                    </div>
                    {itemErrors[it.id] && <p className="pin-error" role="alert">{itemErrors[it.id]}</p>}
                  </div>
                );
              })}

              <form className="conf-item-editor conf-item-new" onSubmit={handleAddItem}>
                <h3 className="conf-new-item-title">項目を追加</h3>
                <div className="admin-lock-row">
                  <input
                    type="text" className="admin-password-input" placeholder="項目名"
                    value={newItemDraft.label}
                    onChange={(e) => setNewItemDraft((d) => ({ ...d, label: e.target.value }))}
                  />
                  <label className="conf-style-check">
                    <input
                      type="checkbox"
                      checked={newItemDraft.style === 'narrative'}
                      onChange={(e) => setNewItemDraft((d) => ({ ...d, style: e.target.checked ? 'narrative' : 'bullet' }))}
                    />
                    文章でまとめる
                  </label>
                </div>
                <textarea
                  className="editor-textarea conf-item-prompt"
                  placeholder="この項目をどうまとめるか（プロンプト）"
                  value={newItemDraft.prompt}
                  onChange={(e) => setNewItemDraft((d) => ({ ...d, prompt: e.target.value }))}
                />
                <div className="admin-save-row">
                  <button className="enter admin-save-btn" type="submit" disabled={!newItemDraft.label.trim() || itemBusyId === '__new__'}>
                    {itemBusyId === '__new__' ? '追加中…' : 'この項目を追加'}
                  </button>
                </div>
                {itemErrors.__new__ && <p className="pin-error" role="alert">{itemErrors.__new__}</p>}
              </form>
            </div>
          )}
        </>
      )}
    </div>
  );
}
